import { useQuery } from '@apollo/client';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useSchool } from '../session/SessionProvider';
import { GET_SUBSCRIPTION_HISTORIES_COUNT, GetActiveSubscriptionHistoriesCountVariables, GetSubscriptionHistoriesCountResponse } from './gql';

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

interface SubscriptionContextType {
    subscribed: boolean | undefined;
}

export const SubscriptionProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
    const { school } = useSchool();
    const [subscribed, setSubscribed] = useState<boolean | undefined>(undefined);

    /**
     * Note that we are using device local time for query, it is for simplicity purpose.
     *
     * It is ok to do that as, for now, it is all for frontend display.
     * The actual subscription check for feature gating is already done in backend when user tries to access the feature,
     * so it is not a problem if the frontend display is off.
     *
     * If we want to make it more accurate, we can consider getting the server time from our backend and do the subscription check based on that.
     */
    const now = useMemo(() => new Date().toISOString(), []);
    const { data: subscriptionCount } = useQuery<GetSubscriptionHistoriesCountResponse, GetActiveSubscriptionHistoriesCountVariables>(GET_SUBSCRIPTION_HISTORIES_COUNT, {
        variables: {
            where: {
                school: {
                    id: {
                        equals: school?.id || '',
                    },
                },
                expiryDate: {
                    gt: now,
                },
            },
        },
        skip: !school?.id,
        fetchPolicy: 'network-only',
    });

    useEffect(() => {
        if (subscriptionCount) {
            setSubscribed(subscriptionCount.subscriptionHistoriesCount > 0);
        }
    }, [subscriptionCount]);

    return (
        <SubscriptionContext.Provider
            value={{
                subscribed,
            }}
        >
            {children}
        </SubscriptionContext.Provider>
    );
};

export const useSubscription = (): SubscriptionContextType => {
    const context = useContext(SubscriptionContext);
    if (!context) {
        throw new Error('useSubscription must be used within a SubscriptionProvider');
    }

    return context;
};
